import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { assign } from 'lodash'
import { Card, Button, Switch } from '@material-ui/core'
import FormItem from 'components/FormItem'
import { isBlank } from 'common/usual'
import {
  brazeCampaignDetailPromise,
  brazeCampaignCreatePromise,
  brazeCampaignUpdatePromise,
} from 'api/BrazeCampaign'

export default function BrazeCampaignUpsert (props) {
  const id = props.match.params.id
  const [campaign, setCampaign] = useState({ active: true })
  const [btnStatus, setBtnStatus] = useState(false)

  const disableBtn = useMemo(() => (
    btnStatus ||
      isBlank(campaign.name) ||
      isBlank(campaign.campaignKey) ||
      isBlank(campaign.brazeCampaignId)
  ), [btnStatus, campaign.name, campaign.campaignKey, campaign.brazeCampaignId])

  const getDetail = useCallback(async () => {
    if (!id) return
    setCampaign(await brazeCampaignDetailPromise(id))
  }, [id])

  useEffect(() => { getDetail() }, [getDetail])

  const handleChange = useCallback((...values) => {
    setCampaign(current => assign({}, current, ...values))
  }, [])

  const commonProps = useMemo(() => ({
    onChange: handleChange,
    data: campaign
  }), [handleChange, campaign])

  function handleSubmit () {
    setBtnStatus(true)

    const body = {
      msgTemplateId: campaign.msgTemplateId ? Number(campaign.msgTemplateId) : undefined,
      name: campaign.name,
      campaignKey: campaign.campaignKey,
      messageType: campaign.messageType,
      brazeUrl: campaign.brazeUrl,
      brazeCampaignId: campaign.brazeCampaignId,
      active: !!campaign.active,
      osType: campaign.osType ? Number(campaign.osType) : undefined,
    }

    // `updatedAt` goes on update only -- it is the optimistic-concurrency token the API requires
    // back, and the create schema rejects it as an unknown key.
    const request = id
      ? brazeCampaignUpdatePromise(id, { ...body, updatedAt: campaign.updatedAt })
      : brazeCampaignCreatePromise(body)

    request
      .then(() => props.history.goBack())
      // 409 = the campaign changed under us. Refetch so the form shows the current values and
      // carries a fresh token; resubmitting the old one would fail identically forever.
      .catch(err => { if (err && err.status === 409) return getDetail() })
      .finally(() => setBtnStatus(false))
  }

  return (
    <div className={'root'}>
      <Card className={'form-container'}>
        <FormItem label={'Name'} name={'name'} isRequired {...commonProps} />
        <FormItem label={'Campaign Key'} name={'campaignKey'} isRequired {...commonProps} />
        <FormItem label={'Braze Campaign ID'} name={'brazeCampaignId'} isRequired {...commonProps} />
        <FormItem label={'Message Type'} name={'messageType'} {...commonProps} />
        <FormItem label={'Braze URL'} name={'brazeUrl'} {...commonProps} />
        <FormItem label={'Msg Template ID'} name={'msgTemplateId'} type={'number'} {...commonProps} />
        <FormItem label={'OS Type'} name={'osType'} type={'number'} {...commonProps} />

        <FormItem label={'Active'} name={'active'}>
          <Switch
            checked={Boolean(campaign.active)}
            onChange={event => handleChange({ active: event.target.checked })}
            name='active'
          />
        </FormItem>

        <p className='tips'>
          A change is live in Redis immediately, but each API and mq process keeps its own in-memory
          copy on a 5-minute timer -- allow up to 5 minutes to take effect everywhere.
        </p>

        <Button
          className='save-btn'
          color='primary'
          fullWidth
          size='large'
          type='submit'
          variant='contained'
          onClick={handleSubmit}
          disabled={disableBtn}>
          Save
        </Button>
      </Card>
    </div>
  )
}
